import React, { useState, useEffect } from 'react';
import { FaEdit } from 'react-icons/fa';
import { getContactsByIds } from '../components/utils/pocketbaseApi';
import { useCompany } from '../context/CompanyContext';

const Profile = () => {
  const [activeTab, setActiveTab] = useState('master');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [masterUsers, setMasterUsers] = useState([]);
  const [administrators, setAdministrators] = useState([]);
  const [arcChiefs, setArcChiefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const { currentCompanyId } = useCompany();

  // Toast component
  const Toast = ({ message, type }) => {
    if (!message) return null;
    
    return (
      <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white z-50 transform transition-all duration-300 ${
        type === 'success' ? 'bg-[#A1E744]' : 'bg-red-500'
      }`}>
        {message}
      </div>
    );
  };

  // Function to show toast
  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
        const companyId = currentCompanyId || pbAuth?.record?.CompanyID;
        
        // Fetch company data
        const response = await fetch(
          `${process.env.REACT_APP_PB_URL}/api/collections/Customers/records?filter=id='${companyId}'`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (!response.ok) throw new Error('Failed to fetch company data');
        const data = await response.json();
        const company = data.items[0];
        setCompanyData(company);

        // Fetch user data for each role
        if (company.SuperAdmin?.length > 0) {
          try {
            const masterUsersData = await getContactsByIds(company.SuperAdmin);
            console.log("masterUsersData>>>", masterUsersData);
            setMasterUsers(Object.values(masterUsersData));
          } catch (error) {
            console.error("Error fetching master users:", error);
          }
        }
        
        if (company.Administrator?.length > 0) {
          try {
            console.log("company.Administrator>>>", company.Administrator);
            const adminsData = await getContactsByIds(company.Administrator);
            setAdministrators(Object.values(adminsData));
          } catch (error) {
            console.error("Error fetching administrators:", error);
          }
        }
        
        if (company.ARCChief?.length > 0) {
          try {
            const chiefsData = await getContactsByIds(company.ARCChief);
            setArcChiefs(Object.values(chiefsData));
          } catch (error) {
            console.error("Error fetching ARC chiefs:", error);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentCompanyId]);

  const handleEdit = (section, user = null) => {
    setEditingSection(section);
    if (section === 'company') {
      setFormData(companyData || {});
    } else {
      setFormData(user || {});
    }
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    try {
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      const collection = editingSection === 'company' ? 'Customers' : 'Contacts';
      const id = editingSection === 'company' ? (currentCompanyId || pbAuth.record.CompanyID) : formData.id;

      const response = await fetch(
        `${process.env.REACT_APP_PB_URL}/api/collections/${collection}/records/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update data');
      }
      
      // Update local state
      if (editingSection === 'company') {
        setCompanyData(formData);
        showToast('Company information updated successfully', 'success');
      } else {
        // Update the appropriate user state
        const users = activeTab === 'master' ? masterUsers : 
                     activeTab === 'admin' ? administrators : 
                     arcChiefs;
        const updatedUsers = users.map(user => 
          user.id === formData.id ? { ...user, ...formData } : user
        );
        
        if (activeTab === 'master') {
          setMasterUsers(updatedUsers);
          showToast('Master user information updated successfully', 'success');
        } else if (activeTab === 'admin') {
          setAdministrators(updatedUsers);
          showToast('Administrator information updated successfully', 'success');
        } else {
          setArcChiefs(updatedUsers);
          showToast('ARC Chief information updated successfully', 'success');
        }
      }
      
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error updating data:', error);
      showToast(error.message || 'Failed to update information. Please try again.', 'error');
    }
  };

  const renderUserInfo = (users) => {
    if (loading) {
      return (
        <div className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Surname</label>
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      );
    }

    if (!users || users.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6">
          <p className="text-gray-500 text-center">No users found</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle space-y-4">
            {users.map((user, index) => (
              <div key={user.id || index} className="relative bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6 hover:border-[#A1E744] transition-colors duration-200">
                <button
                  onClick={() => handleEdit('contact', user)}
                  className="absolute top-4 right-4 flex items-center text-[#A1E744] hover:text-[#0a1744] transition-colors duration-200"
                >
                  <FaEdit className="mr-2" />
                  Edit
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="mt-1 text-gray-900">{user.Name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Surname</label>
                    <p className="mt-1 text-gray-900">{user.Surname}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-gray-900">{user.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                    <p className="mt-1 text-gray-900">{user.MobileNumber}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderCompanyInfo = () => {
    console.log("companyData>>>", companyData);
    return (
      <div className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6 hover:border-[#A1E744] transition-colors duration-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Legal Name</label>
            {loading || !companyData ? (
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            ) : (
              <p className="mt-1 text-gray-900">{companyData.CompanyName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Business Name</label>
            {loading || !companyData ? (
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            ) : (
              <p className="mt-1 text-gray-900">{companyData.CommercialName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Address</label>
            {loading || !companyData ? (
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            ) : (
              <p className="mt-1 text-gray-900">{companyData.CompanyAddress}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Main Telephone</label>
            {loading || !companyData ? (
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            ) : (
              <p className="mt-1 text-gray-900">{companyData.Telephone1}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Invoice Address</label>
            {loading || !companyData ? (
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            ) : (
              <p className="mt-1 text-gray-900">{companyData.InvoiceAddress}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Legal Business Number</label>
            {loading || !companyData ? (
              <div className="mt-1 h-6 bg-gray-200 rounded animate-pulse"></div>
            ) : (
              <p className="mt-1 text-gray-900">{companyData?.LegalBN}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <h1 className="text-4xl font-bold text-[#0a1744] bg-gradient-to-r from-[#0a1744] to-[#A1E744] bg-clip-text text-transparent mb-8">Profile</h1>
      
      {/* Toast Notification */}
      {toast.show && <Toast message={toast.message} type={toast.type} />}

      {/* Company Information */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-[#0a1744]">Company Information</h2>
          <button
            onClick={() => handleEdit('company')}
            className="flex items-center text-[#A1E744] hover:text-[#0a1744] transition-colors duration-200"
          >
            <FaEdit className="mr-2" />
            Edit
          </button>
        </div>
        {renderCompanyInfo()}
      </div>

      {/* Contact Information */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-[#0a1744]">Contact Information</h2>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto pb-2 scrollbar-hide">
            {[
              { id: 'master', label: 'Master Users' },
              { id: 'admin', label: 'Administrators' },
              { id: 'arc', label: 'ARC Chiefs' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex-shrink-0
                  ${activeTab === tab.id
                    ? 'border-[#A1E744] text-[#A1E744]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="relative">
          <div>
            {activeTab === 'master' && renderUserInfo(masterUsers)}
            {activeTab === 'admin' && renderUserInfo(administrators)}
            {activeTab === 'arc' && renderUserInfo(arcChiefs)}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-xl font-bold mb-4 text-[#0a1744]">
              Edit {editingSection === 'company' ? 'Company' : 'Contact'} Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {editingSection === 'company' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Legal Name</label>
                    <input
                      type="text"
                      value={formData.CompanyName || ''}
                      onChange={(e) => setFormData({ ...formData, CompanyName: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Business Name</label>
                    <input
                      type="text"
                      value={formData.CommercialName || ''}
                      onChange={(e) => setFormData({ ...formData, CommercialName: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <input
                      type="text"
                      value={formData.CompanyAddress || ''}
                      onChange={(e) => setFormData({ ...formData, CompanyAddress: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Main Telephone</label>
                    <input
                      type="text"
                      value={formData.Telephone1 || ''}
                      onChange={(e) => setFormData({ ...formData, Telephone1: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Invoice Address</label>
                    <input
                      type="text"
                      value={formData.InvoiceAddress || ''}
                      onChange={(e) => setFormData({ ...formData, InvoiceAddress: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Legal Business Number</label>
                    <input
                      type="text"
                      value={formData.LegalBN || ''}
                      onChange={(e) => setFormData({ ...formData, LegalBN: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={formData.Name || ''}
                      onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Surname</label>
                    <input
                      type="text"
                      value={formData.Surname || ''}
                      onChange={(e) => setFormData({ ...formData, Surname: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                    <input
                      type="tel"
                      value={formData.MobileNumber || ''}
                      onChange={(e) => setFormData({ ...formData, MobileNumber: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors duration-200"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 